// Pure decision logic for what the app should do on load, based on the URL
// hash + query produced by a Supabase password-recovery link.
//
// Kept framework-free (no React/supabase imports) so it can be unit-tested
// deterministically — the preview environment sanitizes URL params before
// React mounts, so the only reliable way to verify this branching is in tests.

export const RECOVERY_EXPIRED_MSG =
  'This password reset link has expired or was already opened. Some email systems automatically scan links, which can use them up before you click. Please request a new link below and open it right away.';

export const RECOVERY_INVALID_MSG =
  'This password reset link is no longer valid. Please request a new one below.';

export const RECOVERY_VERIFY_FAILED_MSG =
  'This password reset link has expired or was already used. Please request a new one below and open it right away.';

/**
 * @param {string} hashStr   - window.location.hash (e.g. "#error=...&error_code=otp_expired")
 * @param {string} searchStr - window.location.search (e.g. "?token_hash=...&type=recovery")
 * @returns {{kind: 'error'|'token_hash'|'legacy'|'legacy_no_tokens'|'none', message?: string, tokenHash?: string, accessToken?: string, refreshToken?: string}}
 */
export function parseRecoveryFromUrl(hashStr = '', searchStr = '') {
  // Normalize double-hash URLs — GoTrue appends its own fragment to
  // redirect_to, so links from older emails arrive as
  // '#type=recovery#access_token=...'. Merge every '#' chunk into one
  // param set (later chunks win) so tokens always parse.
  const h = new URLSearchParams();
  for (const chunk of (hashStr || '').replace(/^#/, '').split('#').filter(Boolean)) {
    new URLSearchParams(chunk).forEach((v, k) => h.set(k, v));
  }
  const q = new URLSearchParams((searchStr || '').replace(/^\?/, ''));

  // (1) Dead-link error path: Supabase redirects consumed/expired recovery
  // links here with error params (in the hash for implicit, query for PKCE).
  const errCode = h.get('error_code') || q.get('error_code');
  const errRaw = h.get('error') || q.get('error');
  if (errCode || errRaw) {
    const desc = h.get('error_description') || q.get('error_description') || '';
    const expired = errCode === 'otp_expired' || /expired|invalid/i.test(desc);
    return { kind: 'error', message: expired ? RECOVERY_EXPIRED_MSG : RECOVERY_INVALID_MSG };
  }

  // (2) Scanner-resistant flow: token_hash carried as inert query data.
  const tokenHash = q.get('token_hash');
  if (tokenHash && q.get('type') === 'recovery') {
    return { kind: 'token_hash', tokenHash };
  }

  // (3) Legacy implicit-flow recovery (hash tokens) — backward compatible.
  // Tokens may also arrive WITHOUT an explicit type=recovery pair when the
  // fragment was mangled — trust access+refresh tokens with type recovery
  // or no conflicting type.
  const accessToken = h.get('access_token');
  const refreshToken = h.get('refresh_token');
  const hType = h.get('type');
  if (accessToken && refreshToken && (hType === 'recovery' || !hType)) {
    return { kind: 'legacy', accessToken, refreshToken };
  }
  if (hType === 'recovery') {
    return { kind: 'legacy_no_tokens' };
  }

  return { kind: 'none' };
}
